import { Theme } from '@mui/material';
import { makeStyles } from '@mui/styles';

import { CS_FONT, CS_NARROW, CS_WEIGHT, csTokens } from 'components/CallScribe/callScribeTheme';

export const paperFieldRules = (theme: Theme): Record<string, unknown> => {
  const t = csTokens(theme);
  return {
    minWidth: 0,
    '& .MuiInputLabel-root, & .MuiInputLabel-root.Mui-focused, & .MuiInputLabel-root.MuiInputLabel-shrink': {
      position: 'relative',
      transform: 'none',
      ...CS_FONT.caption,
      fontWeight: CS_WEIGHT.strong,
      letterSpacing: '0.02em',
      color: t.text2,
      margin: '0 0 6px 2px',
      maxWidth: '100%',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    },
    '& .MuiInputLabel-root.Mui-disabled': { color: t.text2 },
    '& .MuiInputBase-root': {
      borderRadius: 12,
      background: t.surface,
      color: t.text,
      fontFamily: 'inherit',
      ...CS_FONT.body,
    },
    '& .MuiOutlinedInput-notchedOutline': {
      top: 0,
      borderColor: t.fieldLine,
      transition: 'border-color 150ms ease-out',
      '& legend': { display: 'none' },
    },
    '& .MuiInputBase-root:hover .MuiOutlinedInput-notchedOutline': { borderColor: t.text2 },
    '& .MuiInputBase-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: t.focus,
      borderWidth: 2,
    },
    '& .MuiInputBase-root.Mui-error .MuiOutlinedInput-notchedOutline': { borderColor: t.neg },
    '& .MuiInputBase-root.Mui-disabled': { background: t.surface2 },
    '& .MuiInputBase-root.Mui-disabled .MuiOutlinedInput-notchedOutline': {
      borderColor: t.lineStrong,
      borderStyle: 'dashed',
    },
    '& .MuiInputBase-input.Mui-disabled': { WebkitTextFillColor: t.text2, color: t.text2 },
    '& .MuiOutlinedInput-input': {
      padding: '13px 14px',
      height: 'auto',
      ...CS_FONT.body,
      fontWeight: CS_WEIGHT.strong,
      fontVariantNumeric: 'tabular-nums',
    },
    '& .MuiSelect-select.MuiOutlinedInput-input': {
      minHeight: 22,
      paddingRight: 36,
    },
    '& .MuiInputBase-multiline': { padding: '10px 12px' },
    '& .MuiInputBase-multiline .MuiOutlinedInput-input': {
      padding: 0,
      fontWeight: CS_WEIGHT.regular,
    },
    '& .MuiInputBase-input::placeholder': { color: t.text3, opacity: 1 },
    '& .MuiSelect-icon': { color: t.text2 },
    '& .MuiFormHelperText-root': {
      ...CS_FONT.caption,
      color: t.text2,
      margin: '6px 2px 0',
    },
  };
};

const usePaperStyles = makeStyles((theme: Theme) => {
  const t = csTokens(theme);
  const tone = (bg: string, fg: string): Record<string, unknown> => ({
    background: bg,
    '& $noticeTitle': { color: fg },
  });
  return {
    surface: {
      isolation: 'isolate',
      background: t.bg,
      color: t.text,
      fontFamily: "'Nunito', sans-serif",
    },
    section: {
      background: t.surface,
      border: `1px solid ${t.line}`,
      borderRadius: 14,
      padding: 16,
      display: 'flex',
      flexDirection: 'column',
      gap: 12,
      minWidth: 0,
    },
    sectionHead: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      columnGap: 12,
      rowGap: 4,
      flexWrap: 'wrap',
      minWidth: 0,
      minHeight: 24,
    },
    sectionLabel: {
      ...CS_FONT.caption,
      margin: 0,
      fontWeight: CS_WEIGHT.heavy,
      letterSpacing: '0.08em',
      textTransform: 'uppercase',
      color: t.text2,
      display: 'flex',
      alignItems: 'center',
      gap: 8,
      minWidth: 0,
    },
    sectionAside: {
      ...CS_FONT.caption,
      fontWeight: CS_WEIGHT.strong,
      color: t.text2,
      fontVariantNumeric: 'tabular-nums',
      marginLeft: 'auto',
    },
    body: {
      ...CS_FONT.body,
      margin: 0,
      color: t.text,
      whiteSpace: 'pre-wrap',
      wordBreak: 'break-word',
      maxWidth: '68ch',
    },
    small: {
      ...CS_FONT.small,
      margin: 0,
      color: t.text2,
    },
    caption: {
      ...CS_FONT.caption,
      margin: 0,
      color: t.text2,
    },
    field: paperFieldRules(theme),
    fieldGrid: {
      display: 'grid',
      gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)',
      gap: 12,
      alignItems: 'start',
      minWidth: 0,
      '& > *': { minWidth: 0 },
      [CS_NARROW]: { gridTemplateColumns: 'minmax(0, 1fr)' },
    },
    fullRow: {
      gridColumn: '1 / -1',
    },
    subStack: {
      display: 'flex',
      flexDirection: 'column',
      gap: 10,
      minWidth: 0,
    },
    quote: {
      display: 'flex',
      flexDirection: 'column',
      gap: 2,
      padding: '2px 0 2px 12px',
      borderLeft: `3px solid ${t.lineStrong}`,
      '& p': { whiteSpace: 'pre-wrap', wordBreak: 'break-word' },
    },
    linkButton: {
      alignSelf: 'flex-start',
      display: 'inline-flex',
      alignItems: 'center',
      gap: 4,
      minHeight: 32,
      padding: '0 8px 0 4px',
      marginLeft: -4,
      border: 'none',
      borderRadius: 8,
      background: 'transparent',
      color: t.info,
      fontFamily: 'inherit',
      ...CS_FONT.small,
      fontWeight: CS_WEIGHT.strong,
      cursor: 'pointer',
      '& svg': { fontSize: 18 },
      '&:hover': { background: t.infoBg },
      '&:focus-visible': { outline: `2px solid ${t.focus}`, outlineOffset: 2 },
    },
    readOnlyChip: {
      ...CS_FONT.caption,
      lineHeight: '20px',
      height: 20,
      padding: '0 6px',
      borderRadius: 6,
      boxShadow: `inset 0 0 0 1px ${t.lineStrong}`,
      color: t.text2,
      fontWeight: CS_WEIGHT.strong,
      letterSpacing: '0.04em',
      textTransform: 'uppercase',
    },
    readOnlyBlock: {
      ...CS_FONT.body,
      margin: 0,
      color: t.text2,
      background: t.surface2,
      border: `1px dashed ${t.lineStrong}`,
      borderRadius: 12,
      padding: '10px 12px',
      whiteSpace: 'pre-wrap',
      wordBreak: 'break-word',
      '&:focus-visible': { outline: `2px solid ${t.focus}`, outlineOffset: 2 },
    },
    notice: {
      borderRadius: 14,
      padding: '12px 14px',
      display: 'flex',
      flexDirection: 'column',
      gap: 4,
      minWidth: 0,
      background: t.surface2,
      border: `1px solid ${t.line}`,
    },
    noticeTitle: {
      ...CS_FONT.small,
      margin: 0,
      fontWeight: CS_WEIGHT.heavy,
      color: t.text,
      display: 'flex',
      alignItems: 'center',
      gap: 6,
      '& svg': { fontSize: 18, flex: '0 0 auto' },
    },
    noticeBody: {
      ...CS_FONT.small,
      margin: 0,
      color: t.text,
      whiteSpace: 'pre-wrap',
      wordBreak: 'break-word',
    },
    noticeInfo: { ...tone(t.infoBg, t.info), borderColor: 'transparent' },
    noticeWarn: { ...tone(t.warnBg, t.warn), borderColor: 'transparent' },
    noticeBad: { ...tone(t.negBg, t.neg), borderColor: 'transparent' },
    noticeGood: { ...tone(t.posBg, t.pos), borderColor: 'transparent' },
    textButton: {
      alignSelf: 'flex-start',
      display: 'inline-flex',
      alignItems: 'center',
      gap: 6,
      minHeight: 36,
      marginTop: 4,
      padding: '0 12px',
      borderRadius: 10,
      border: `1px solid ${t.controlLine}`,
      background: t.surface,
      color: t.text,
      fontFamily: 'inherit',
      ...CS_FONT.small,
      fontWeight: CS_WEIGHT.strong,
      cursor: 'pointer',
      transition: 'background-color 150ms ease-out',
      '& svg': { fontSize: 16 },
      '&:hover': { background: t.surface3 },
      '&:focus-visible': { outline: `2px solid ${t.focus}`, outlineOffset: 2 },
      '&:disabled, &[aria-disabled="true"]': {
        cursor: 'not-allowed',
        color: t.text2,
        background: t.surface2,
      },
    },
    actions: {
      display: 'flex',
      alignItems: 'center',
      gap: 8,
      paddingTop: 4,
      flexWrap: 'wrap',
    },
    primaryButton: {
      flex: '1 1 auto',
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 8,
      minHeight: 44,
      padding: '0 16px',
      border: 'none',
      borderRadius: 12,
      background: t.call,
      color: t.callInk,
      fontFamily: 'inherit',
      fontSize: 15,
      lineHeight: '22px',
      fontWeight: CS_WEIGHT.strong,
      cursor: 'pointer',
      transition: 'background-color 150ms ease-out, transform 120ms ease-out',
      '&:active': { transform: 'scale(0.99)' },
      '&:focus-visible': { outline: `2px solid ${t.focus}`, outlineOffset: 2 },
      '&:disabled': { cursor: 'not-allowed', background: t.surface3, color: t.text2, transform: 'none' },
      '@media (prefers-reduced-motion: reduce)': { '&:active': { transform: 'none' } },
    },
    ghostButton: {
      flex: '0 0 auto',
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 6,
      minHeight: 44,
      minWidth: 44,
      padding: '0 12px',
      border: 'none',
      borderRadius: 12,
      background: 'transparent',
      color: t.text2,
      fontFamily: 'inherit',
      ...CS_FONT.small,
      fontSize: 14,
      fontWeight: CS_WEIGHT.strong,
      cursor: 'pointer',
      transition: 'background-color 150ms ease-out, color 150ms ease-out',
      '& svg': { fontSize: 20 },
      '&:hover': { background: t.surface3, color: t.text },
      '&:focus-visible': { outline: `2px solid ${t.focus}`, outlineOffset: 2 },
      '&:disabled': { cursor: 'not-allowed', color: t.text3, background: 'transparent' },
    },
    checkRow: {
      marginLeft: -6,
      marginRight: 0,
      '& .MuiFormControlLabel-label': {
        ...CS_FONT.small,
        fontWeight: CS_WEIGHT.strong,
        color: t.text,
      },
      '& .MuiCheckbox-root': { color: t.text2, padding: 8 },
      '& .MuiCheckbox-root.Mui-checked': { color: t.focus },
      '& .MuiCheckbox-root.Mui-focusVisible': { outline: `2px solid ${t.focus}`, outlineOffset: -4, borderRadius: 8 },
    },
    srOnly: {
      position: 'absolute',
      width: 1,
      height: 1,
      padding: 0,
      margin: -1,
      overflow: 'hidden',
      clip: 'rect(0, 0, 0, 0)',
      whiteSpace: 'nowrap',
      border: 0,
    },
  };
});

export default usePaperStyles;
